import React, { Fragment, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { resolveTool, SUGGESTIONS } from "../../helpers/boAgentTools";

/**
 * Bo Agent — a slide-out assistant panel.
 *
 * The UI is the long-lived part. Answers currently come from a keyword
 * matcher over a tool registry (helpers/boAgentTools.js); when a real model
 * is wired in it consumes the SAME registry as its tool schema and this
 * component does not change.
 *
 * Placement note: the panel slides from the right rather than using a
 * bottom-right bubble, because index.html already loads a third-party
 * support widget in that corner.
 *
 * Read-only by construction — every tool in the registry is a GET. The agent
 * cannot suspend a card or approve a deposit.
 */
const BoAgent = ({ open, onClose }) => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const clientId = currentUser?.role?.includes("Client admin")
    ? currentUser?.user?.ClientId
    : "";

  const firstName = (currentUser?.user?.Name || "").split(" ")[0];

  // greet once, when the panel is first opened
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
        {
          from: "bo",
          text: `Hello${firstName ? " " + firstName : ""} — I can look things up across the platform. Ask me about cards, clients, deposits or platform health.`,
        },
      ]);
    }
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current && inputRef.current.focus(), 260);
    }
  }, [open, messages.length, firstName]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinking]);

  // close on Escape
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const ask = (question) => {
    const q = (question || "").trim();
    if (!q || thinking) return;

    setMessages((m) => [...m, { from: "me", text: q }]);
    setInput("");
    setThinking(true);

    const tool = resolveTool(q);

    // a short delay so the answer does not snap in faster than the operator
    // can read their own question back
    const minDelay = new Promise((r) => setTimeout(r, 480));

    const work = tool
      ? tool.run({ clientId, question: q }).catch(() => ({
          text: "I could not reach that service just now. Please try again.",
        }))
      : Promise.resolve({
          text:
            "I can't answer that yet. Right now I can report on cards, clients, deposits, PIN requests, transaction volume and platform health.",
        });

    Promise.all([work, minDelay]).then(([answer]) => {
      setThinking(false);
      setMessages((m) => [...m, { from: "bo", ...answer, tool: tool?.name }]);
    });
  };

  /* Rendered through a portal into <body>.
     The nav mounts this component, and .bopay-nav carries overflow-y:auto
     (so a long nav scrolls internally) — a fixed-position panel rendered
     inside it would be clipped by that scroll container on mobile. A portal
     sidesteps the whole question. */
  return ReactDOM.createPortal(
    <Fragment>
      <div
        className={"bopay-agent-scrim" + (open ? " is-open" : "")}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className={"bopay-agent" + (open ? " is-open" : "")}
        role="dialog"
        aria-label="Bo AI Agent"
        aria-hidden={!open}
      >
        <header className="bopay-agent-head">
          <div className="bopay-agent-id">
            <span className="bopay-agent-avatar" aria-hidden="true">bo</span>
            <div>
              <p className="bopay-agent-name">
                Bo AI Agent
                <span className="bopay-agent-beta">beta</span>
              </p>
              <p className="bopay-agent-state">
                <span className="bopay-agent-online" aria-hidden="true"></span>
                Connected · read-only
              </p>
            </div>
          </div>
          <button type="button" className="bopay-agent-close"
                  onClick={onClose} aria-label="Close assistant">
            <span aria-hidden="true"></span>
          </button>
        </header>

        <div className="bopay-agent-thread">
          {/* States the limitation where someone is actually forming an
              opinion — a badge in the nav alone is easy to miss, and the
              name says "AI" while the answers are currently matched to a
              fixed set of tools rather than generated by a model. */}
          <p className="bopay-agent-notice">
            Beta — answers come from live platform data across a fixed set of
            lookups. Full AI understanding is in development.
          </p>

          {messages.map((m, i) => (
            <div key={i} className={"bopay-agent-msg bopay-agent-msg--" + m.from}>
              <div className="bopay-agent-bubble">
                <p className="bopay-agent-text">{m.text}</p>

                {m.detail && (
                  <dl className="bopay-agent-detail">
                    {m.detail.map((d) => (
                      <div key={d.label}>
                        <dt>{d.label}</dt>
                        <dd>{d.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}

                {m.link && (
                  /* linkState carries router state for screens that read it
                     (e.g. /cardholderdetails expects { id, userId,
                     issuerCategory } exactly as the grids pass it) */
                  <Link
                    to={
                      m.linkState
                        ? { pathname: m.link, state: m.linkState }
                        : m.link
                    }
                    className="bopay-agent-link"
                    onClick={onClose}
                  >
                    {m.linkLabel || "Open"}
                    <span className="bopay-agent-link-go" aria-hidden="true"></span>
                  </Link>
                )}
              </div>
            </div>
          ))}

          {thinking && (
            <div className="bopay-agent-msg bopay-agent-msg--bo">
              <div className="bopay-agent-bubble bopay-agent-bubble--typing">
                <span></span><span></span><span></span>
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>

        {messages.length <= 1 && (
          <div className="bopay-agent-suggest">
            {SUGGESTIONS.map((s) => (
              <button key={s} type="button" className="bopay-agent-chip"
                      onClick={() => ask(s)}>
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          className="bopay-agent-compose"
          onSubmit={(e) => { e.preventDefault(); ask(input); }}
        >
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask Bo a question…"
            aria-label="Ask Bo a question"
          />
          <button type="submit" disabled={!input.trim() || thinking}
                  aria-label="Send">
            <span className="bopay-agent-send" aria-hidden="true"></span>
          </button>
        </form>
      </aside>
    </Fragment>,
    document.body
  );
};

export default BoAgent;
